/**
 * Alap matematika. Minden 2D művelet az XZ-síkon értendő (Y = magasság).
 * Gazda: C
 */

export interface Vec2 {
  x: number
  z: number
}

export const TAU = Math.PI * 2
export const DEG = Math.PI / 180

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** Keretidő-független simítás. `rate` = mennyi maradjon 1 másodperc alatt. */
export function damp(a: number, b: number, rate: number, dt: number): number {
  return lerp(a, b, 1 - Math.pow(rate, dt))
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1)
  return t * t * (3 - 2 * t)
}

/** Szöget a (-PI, PI] tartományba normalizál. */
export function wrapAngle(a: number): number {
  a = (a + Math.PI) % TAU
  if (a < 0) a += TAU
  return a - Math.PI
}

/** Legrövidebb szögkülönbség a-tól b-ig. */
export function angleDelta(a: number, b: number): number {
  return wrapAngle(b - a)
}

/** Szög a-tól b felé, legfeljebb `maxStep` radiánnal. */
export function turnToward(a: number, b: number, maxStep: number): number {
  const d = angleDelta(a, b)
  return wrapAngle(a + clamp(d, -maxStep, maxStep))
}

export function dist2(ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax
  const dz = bz - az
  return dx * dx + dz * dz
}

export function dist(ax: number, az: number, bx: number, bz: number): number {
  return Math.sqrt(dist2(ax, az, bx, bz))
}

/** Egy pont legközelebbi pontja az [a,b] szakaszon; az eredmény `out`-ba kerül. */
export function closestPointOnSegment(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
  out: Vec2,
): number {
  const abx = bx - ax
  const abz = bz - az
  const lenSq = abx * abx + abz * abz
  let t = lenSq > 1e-12 ? ((px - ax) * abx + (pz - az) * abz) / lenSq : 0
  t = clamp(t, 0, 1)
  out.x = ax + abx * t
  out.z = az + abz * t
  return t
}

/**
 * Sugár–szakasz metszés az XZ-síkon.
 * @returns a sugár menti távolság, vagy -1 ha nincs metszés.
 */
export function raySegment(
  ox: number,
  oz: number,
  dx: number,
  dz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const sx = bx - ax
  const sz = bz - az
  const denom = dx * sz - dz * sx
  if (Math.abs(denom) < 1e-9) return -1 // párhuzamos
  const qx = ax - ox
  const qz = az - oz
  const t = (qx * sz - qz * sx) / denom // sugár menti
  const u = (qx * dz - qz * dx) / denom // szakasz menti
  if (t < 0 || u < 0 || u > 1) return -1
  return t
}

/** Poligon területe (előjeles). Pozitív = CCW a szokásos XZ-tájolásban. */
export function polygonArea(poly: readonly Vec2[]): number {
  let a = 0
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    a += poly[j].x * poly[i].z - poly[i].x * poly[j].z
  }
  return a * 0.5
}

/** Pont-poligon teszt (ray casting). */
export function pointInPolygon(px: number, pz: number, poly: readonly Vec2[]): boolean {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x
    const zi = poly[i].z
    const xj = poly[j].x
    const zj = poly[j].z
    if (zi > pz !== zj > pz && px < ((xj - xi) * (pz - zi)) / (zj - zi) + xi) {
      inside = !inside
    }
  }
  return inside
}

export function polygonCentroid(poly: readonly Vec2[]): Vec2 {
  let cx = 0
  let cz = 0
  let a = 0
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const cross = poly[j].x * poly[i].z - poly[i].x * poly[j].z
    a += cross
    cx += (poly[j].x + poly[i].x) * cross
    cz += (poly[j].z + poly[i].z) * cross
  }
  if (Math.abs(a) < 1e-9) {
    // Elfajult poligon: essünk vissza a csúcsok átlagára.
    for (const p of poly) {
      cx += p.x
      cz += p.z
    }
    return { x: cx / poly.length, z: cz / poly.length }
  }
  a *= 0.5
  return { x: cx / (6 * a), z: cz / (6 * a) }
}
